import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "../../lib/api";
import { ReviewDialog } from "./LandingBottom";

function ReviewCard({ r }) {
  return (
    <div className="glass mx-2.5 flex h-[220px] w-[300px] shrink-0 flex-col rounded-2xl p-5">
      <div className="mb-2.5 flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} size={13} className={i < (r.stars || 0) ? "fill-gold text-gold" : "text-mist-muted/30"} />
        ))}
      </div>
      <p className="line-clamp-4 flex-1 text-sm leading-relaxed text-mist">“{r.text}”</p>
      <div className="mt-3 border-t border-white/[0.06] pt-3">
        <div className="truncate text-sm font-semibold text-mist">{r.name}</div>
        {r.location && <div className="truncate text-xs text-mist-muted">{r.location}</div>}
      </div>
    </div>
  );
}

export function SocialProof() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const load = () => api.reviews().then((r) => setReviews(Array.isArray(r) ? r : [])).catch(() => {});
  useEffect(() => {
    load();
    api.publicStats().then(setStats).catch(() => {});
  }, []);

  const count = reviews.length;
  const avg = count ? reviews.reduce((s, r) => s + (r.stars || 0), 0) / count : 0;
  // Duplicate the list so the marquee loops seamlessly. Need a few cards to fill the row.
  const loop = count ? [...reviews, ...reviews, ...reviews] : [];

  return (
    <section id="reviews" className="relative overflow-hidden py-24">
      <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
        <p className="eyebrow">From our members</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-5xl">Loved across the weave</h2>

        {/* trust row */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3 sm:gap-4" data-testid="trust-row">
          {count > 0 && (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-mist" data-testid="trust-rating">
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(avg) ? "fill-gold text-gold" : "text-mist-muted/30"} />
                ))}
              </span>
              <span className="font-semibold">{avg.toFixed(1)}</span>
              <span className="text-mist-muted">· {count} {count === 1 ? "review" : "reviews"}</span>
            </div>
          )}
          {stats?.members > 0 && (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-mist" data-testid="trust-members">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
              </span>
              <span className="font-semibold">{stats.members.toLocaleString()}</span>
              <span className="text-mist-muted">members</span>
            </div>
          )}
          {stats?.countries > 0 && (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-mist" data-testid="trust-countries">
              <span className="font-semibold">{stats.countries}</span>
              <span className="text-mist-muted">{stats.countries === 1 ? "country" : "countries"} · growing worldwide</span>
            </div>
          )}
        </div>

        <div className="mt-7 flex justify-center">
          <ReviewDialog onSubmitted={load} />
        </div>
      </div>

      {/* marquee */}
      {count > 0 && (
        <div className="relative mt-14" data-testid="reviews-marquee">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-ink to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-ink to-transparent" />
          <div className="marquee-track flex w-max">
            {loop.map((r, i) => <ReviewCard key={`${r.name}-${i}`} r={r} />)}
          </div>
        </div>
      )}
    </section>
  );
}
